import React from "react";
import { Table } from "antd";
import { collection, getDocs, query, where } from "firebase/firestore";
import useAdminDataStore, { selectCurrentGroupData } from "../../../../../store/adminData/adminData";
import { useQuery } from "@tanstack/react-query";
import { db } from "../../../../../services/firebaseService/connection";
import DescriptionText from "../../../../../CommonComponents/DescriptionText";
import useMembersData from "../../Members/useMembersData";

const fetchRestrictedDMs = async (clubId, groupId) => {
  const q = query(
    collection(db, `channels/${clubId}_channels/groups`),
    where("channelType", "==", "direct"),
    where("groupId", "==", groupId),
  );

  const snapshot = await getDocs(q);
  const restricted = [];

  snapshot.forEach((doc) => {
    const docData = doc.data();
    const { restrictedMembers = [], memberIds = [] } = docData;

    if (!restrictedMembers || restrictedMembers.length === 0) return;

    restrictedMembers.forEach((restrictedId) => {
      const restrictedBy = memberIds.find((id) => id !== restrictedId) || null;
      restricted.push({
        key: `${doc.id}_${restrictedId}`,
        restrictedId,
        restrictedById: restrictedBy,
      });
    });
  });

  return restricted;
};

const RestrictedDMs = () => {
  const groupData = useAdminDataStore(selectCurrentGroupData);
  const clubId = groupData?.clubId;
  const groupId = groupData?.id;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["restrictedDMs", clubId, groupId],
    queryFn: () => fetchRestrictedDMs(clubId, groupId),
    enabled: !!clubId && !!groupId,
  });

  const { getMemberById, membersLoading } = useMembersData();

  const resolveName = (id) => {
    const member = getMemberById(id);
    return member?.name || id;
  };

  const tableData = (data || []).map(
    ({ key, restrictedId, restrictedById }) => ({
      key,
      restricted: resolveName(restrictedId),
      restrictedBy: restrictedById ? resolveName(restrictedById) : "-",
    }),
  );

  const columns = [
    {
      title: "Restricted User",
      dataIndex: "restricted",
      key: "restricted",
    },
    {
      title: "Restricted By",
      dataIndex: "restrictedBy",
      key: "restrictedBy",
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      <DescriptionText showBackground>
        Monitor members with restricted DM access. To address recurring safety
        concerns, you can restrict app access by selecting
        &quot;Lock/Archive&quot; in the Member&apos;s profile.
      </DescriptionText>

      {isError ? (
        <p style={{ color: "#e74c3c", fontSize: "14px", margin: 0 }}>
          Failed to load restricted DMs.
        </p>
      ) : (
        <Table
          bordered
          columns={columns}
          dataSource={tableData}
          loading={isLoading || membersLoading}
          rowKey="key"
          scroll={{ x: "max-content" }}
          pagination={{
            defaultPageSize: 50,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            position: ["bottomLeft"],
          }}
          bodyStyle={{
            fontSize: window.screen.width > 500 ? "18px" : "12px",
            backgroundColor: "#ffffff",
          }}
        />
      )}
    </div>
  );
};

export default RestrictedDMs;
